import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { EmbeddedStatus, KBType } from '@/modules/gpt-chats/models/kb';
import { IGetAllKBResponse } from '@/modules/gpt-chats/types/kb.service.type';
import { aiService } from '@/modules/gpt-chats/services/ai.service';

type UseKbNotificationHandlerProps = {
  kbId: string;
  type: KBType;
};

export const useKbNotificationHandler = ({ kbId, type }: UseKbNotificationHandlerProps) => {
  const queryClient = useQueryClient();

  const handleNotification = useCallback(
    (notificationData: unknown) => {
      try {
        const notice =
          typeof notificationData === 'string' ? JSON.parse(notificationData) : notificationData;

        const denom = JSON.parse(notice?.message?.denormalizedPayload ?? '{}') as {
          meta?: { kb_id?: string; status?: EmbeddedStatus };
        };
        if (!denom?.meta) return;
        const status = denom.meta?.status;
        const denomKbId = denom.meta?.kb_id;
        if (!denomKbId || kbId !== denomKbId || !status) return;
        if (status === EmbeddedStatus.COMPLETED) {
          queryClient.setQueriesData(
            { queryKey: ['kb', type] },
            (prevData: IGetAllKBResponse | undefined) => {
              if (!prevData) return prevData;

              return {
                ...prevData,
                data: {
                  ...prevData.data,
                  knowledge_bases: prevData.data.knowledge_bases.map((item) =>
                    item.id === kbId ? { ...item, embad_status: EmbeddedStatus.COMPLETED } : item
                  ),
                },
              };
            }
          );

          aiService.kb
            .getAllKB({
              kb_type: type,
              ids: [kbId],
              limit: 1,
              offset: 0,
            })
            .then((res) => {
              const latestKB = res.data.knowledge_bases?.[0];
              if (!latestKB) return;

              queryClient.setQueriesData(
                { queryKey: ['kb', type] },
                (prevData: IGetAllKBResponse | undefined) => {
                  if (!prevData) return prevData;

                  return {
                    ...prevData,
                    data: {
                      ...prevData.data,
                      knowledge_bases: prevData.data.knowledge_bases.map((item) =>
                        item.id === kbId ? latestKB : item
                      ),
                    },
                  };
                }
              );
            })
            .catch((err) => {
              console.error(err);
            });
        } else {
          queryClient.setQueriesData(
            { queryKey: ['kb', type] },
            (prevData: IGetAllKBResponse | undefined) => {
              if (!prevData) return prevData;

              return {
                ...prevData,
                data: {
                  ...prevData.data,
                  knowledge_bases: prevData.data.knowledge_bases.map((item) =>
                    item.id === kbId ? { ...item, embad_status: status } : item
                  ),
                },
              };
            }
          );
        }
      } catch (error) {
        //
      }
    },
    [kbId, type, queryClient]
  );

  return handleNotification;
};
